import React, { useState, useRef } from "react";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { makeRequest2 } from "../../../utils/makeRequest";
import { toastify } from "../../../utils/toast";
import { getItem } from "../../../utils/localStorage";
import CashPaymentModal from "../../../utils/cashPaymentModal";
import styles from "./invoice.module.css";
import {
    resendInvoiceNotification,
    fetchPropertyManagersURL,
    sendInvoiceToManager,
    voidInvoice,
} from '../../../utils/urls';

const CombinedInvoiceActionsDropdown = ({
    invoice,
    facilityId,
    handleDownloadPDF,
    onCashPaymentSuccess,
    onCashPaymentError,
    onInvoiceVoided,
    loading
}) => {
    const [selectedAction, setSelectedAction] = useState(null);
    const [showCashPaymentModal, setShowCashPaymentModal] = useState(false);
    const [showSendManagerDialog, setShowSendManagerDialog] = useState(false);
    const [showAlternativeNumbersDialog, setShowAlternativeNumbersDialog] = useState(false);
    const [managers, setManagers] = useState([]);
    const [selectedManager, setSelectedManager] = useState(null);
    const [loadingManagers, setLoadingManagers] = useState(false);
    const [sendingInvoice, setSendingInvoice] = useState(false);
    const [resending, setResending] = useState(false);
    const [voidingInvoice, setVoidingInvoice] = useState(false);
    const [sendingToAlternative, setSendingToAlternative] = useState(false);
    const [debugResponse, setDebugResponse] = useState(null);
    
    // State for alternative numbers
    const [alternativeNumbers, setAlternativeNumbers] = useState(['']);
    const [currentNumberInput, setCurrentNumberInput] = useState('');
    
    const toast = useRef(null);

    // The payment modal works on a single invoice: show it the combined invoice's
    // number and what can be paid manually. Postpaid power invoices have no manual
    // payment path (they are paid through the power account), so they are left out.
    const manualPaymentInvoice = invoice && {
        ...invoice,
        invoiceNumber: invoice.combinedInvoiceNumber,
        totalAmount: (invoice.invoices || [])
            .filter((entry) => entry.type !== 'power' && ['Unpaid', 'Pending', 'Partially Paid', 'Overdue'].includes(entry.status))
            .reduce(
                (sum, entry) => sum + Math.max(0, (entry.totalAmount || 0) + (entry.balanceBroughtForward || 0) - (entry.amountPaid || 0)),
                0
            ),
        balanceBroughtForward: 0,
        amountPaid: 0,
    };

    const getAvailableActions = () => {
        const baseActions = [
            { label: "Download Invoice", value: "download", icon: "fas fa-download" }
        ];

        // Only add these actions if invoice is not null
        if (invoice) {
            baseActions.push({
                label: "Manual Payment",
                value: "cash-payment",
                icon: "fas fa-money-bill"
            });

            baseActions.push({
                label: "Send to Client",
                value: "resend",
                icon: "fas fa-paper-plane"
            });

            baseActions.push({
                label: "Send to Alternative Number",
                value: "send-alternative",
                icon: "fas fa-user-tie"
            });

            // Add void invoice action (only for non-voided invoices)
            if (invoice.status !== 'voided' && invoice.status !== 'VOIDED' && 
                invoice.status !== 'void' && invoice.status !== 'VOID') {
                baseActions.push({
                    label: "Void Invoice",
                    value: "void-invoice",
                    icon: "fas fa-ban"
                });
            }
        }

        return baseActions;
    };

    const actions = getAvailableActions();

    const handleActionChange = (e) => {
        const action = e.value;
        setSelectedAction(action);

        switch (action) {
            case "cash-payment":
                setShowCashPaymentModal(true);
                break;
            case "resend":
                handleResendInvoice();
                break;
            case "download":
                handleDownloadPDF();
                break;
            case "send-alternative":
                setShowAlternativeNumbersDialog(true);
                // Reset alternative numbers when opening
                setAlternativeNumbers(['']);
                setCurrentNumberInput('');
                break;
            case "void-invoice":
                handleVoidInvoice();
                break;
            default:
                break;
        }

        setTimeout(() => setSelectedAction(null), 500);
    };

    // Helper function to validate phone number
    const validatePhoneNumber = (number) => {
        // Remove all non-digit characters
        const cleanNumber = number.replace(/\D/g, '');
        // Check if it's a valid length (assuming 10-15 digits)
        return cleanNumber.length >= 10 && cleanNumber.length <= 15;
    };

    // Add new number input
    const addNumberInput = () => {
        setAlternativeNumbers([...alternativeNumbers, '']);
    };

    // Remove number input at index
    const removeNumberInput = (index) => {
        if (alternativeNumbers.length > 1) {
            const newNumbers = alternativeNumbers.filter((_, i) => i !== index);
            setAlternativeNumbers(newNumbers);
        }
    };

    // Update number at specific index
    const updateNumberAtIndex = (index, value) => {
        const newNumbers = [...alternativeNumbers];
        newNumbers[index] = value;
        setAlternativeNumbers(newNumbers);
    };

    // Handle sending to alternative numbers
    const handleSendToAlternativeNumbers = async () => {
        // Filter out empty numbers and validate
        const validNumbers = alternativeNumbers
            .filter(num => num.trim() !== '')
            .map(num => num.trim())
            .filter(validatePhoneNumber);

        if (validNumbers.length === 0) {
            toastify("Please enter at least one valid phone number", "warning");
            return;
        }

        const invalidNumbers = alternativeNumbers
            .filter(num => num.trim() !== '')
            .map(num => num.trim())
            .filter(num => !validatePhoneNumber(num));

        if (invalidNumbers.length > 0) {
            toastify(`Invalid phone numbers detected: ${invalidNumbers.join(', ')}`, "error");
            return;
        }

        try {
            setSendingToAlternative(true);
            toastify(`Sending combined invoice to ${validNumbers.length} alternative number(s)...`, "info");

            // Calculate total due
            const calculateTotalDue = () => {
                if (!invoice) return 0;
                const subtotal = invoice.invoices?.reduce((sum, inv) => sum + (parseFloat(inv.amount) || 0), 0) || 0;
                const totalTax = invoice.invoices?.reduce((sum, inv) => sum + (parseFloat(inv.tax) || 0), 0) || 0;
                const totalBalanceBroughtForward = parseFloat(invoice.totalBalanceBroughtForward) || 0;
                const amountPaid = parseFloat(invoice.amountPaid) || 0;
                return subtotal + totalTax + totalBalanceBroughtForward - amountPaid;
            };

            // Prepare payload for alternative numbers
            const payload = {
                invoiceId: invoice._id,
                phoneNumbers: validNumbers,
                unitName: invoice.unit?.name || "Unit",
                facilityName: invoice.facility?.name || "Facility",
                amount: calculateTotalDue(),
                dueDate: invoice.dueDate,
                invoiceNumber: invoice.combinedInvoiceNumber,
                invoiceType: 'combined',
                customerName: `${invoice.customer?.firstName || ''} ${invoice.customer?.lastName || ''}`.trim() || 'Customer'
            };
            
            const response = await makeRequest2(
                `${resendInvoiceNotification}/${facilityId}/alternative`,
                "POST",
                payload
            );

            if (response.success) {
                toastify(`Combined invoice sent successfully to ${validNumbers.length} number(s)!`, "success");
                setShowAlternativeNumbersDialog(false);
                // Reset the form
                setAlternativeNumbers(['']);
            } else {
                toastify(response.message || "Failed to send combined invoice to alternative numbers", "error");
            }
        } catch (error) {
            console.error("Error sending combined invoice to alternative numbers:", error);
            toastify("Failed to send combined invoice to alternative numbers. Please try again.", "error");
        } finally {
            setSendingToAlternative(false);
        }
    };

    const handleVoidInvoice = async () => {
        if (!invoice || !invoice._id) {
            toastify("Invalid invoice data", "error");
            return;
        }

        // Get current user data from storage
        let currentUser = null;
        try {
            // Try different storage keys that might contain user data
            const userId = await getItem('selectedCustomerId') || await getItem('userId') || await getItem('currentUserId');
            const userInfo = await getItem('currentUser') || await getItem('user') || await getItem('userInfo');
            
            if (userInfo) {
                try {
                    currentUser = typeof userInfo === 'string' ? JSON.parse(userInfo) : userInfo;
                } catch (e) {
                    console.warn("Could not parse user info from storage");
                }
            }

            // If we don't have complete user info, create a basic structure
            if (!currentUser || !currentUser.userId) {
                currentUser = {
                    userId: userId || 'system',
                    fullName: 'System Admin',
                    role: 'Property Manager'
                };
            }

        } catch (error) {
            console.error("Error getting user data:", error);
            currentUser = {
                userId: 'system',
                fullName: 'System Admin',
                role: 'Property Manager'
            };
        }

        confirmDialog({
            message: "Are you sure you want to void this combined invoice? This action cannot be undone.",
            header: "Void Combined Invoice Confirmation",
            icon: "pi pi-exclamation-triangle",
            acceptClassName: "p-button-danger",
            acceptLabel: "Yes, Void Invoice",
            rejectLabel: "Cancel",
            accept: async () => {
                try {
                    setVoidingInvoice(true);
                    toastify("Voiding combined invoice...", "info");

                    const payload = {
                        reason: "Voided by property manager",
                        userId: currentUser.userId,
                        userName: currentUser.fullName || currentUser.firstName || currentUser.username || "System Admin",
                        userRole: currentUser.role || "Property Manager",
                    };

                    console.log("🔍 DEBUG - Void combined invoice payload:", payload);
                    
                    const voidUrl = `${voidInvoice}/${facilityId}/${invoice._id}`;
                    console.log("🔍 DEBUG - Void URL:", voidUrl);

                    const response = await makeRequest2(voidUrl, "POST", payload);
                    
                    console.log("🔍 DEBUG - Void response:", response);

                    if (response.success || response.data) {
                        toastify("Combined invoice successfully voided", "success");
                        // Call the callback to refresh the invoice data
                        if (typeof onInvoiceVoided === 'function') {
                            onInvoiceVoided();
                        }
                    } else {
                        const errorMessage = response.error || response.message || "Failed to void combined invoice";
                        console.error("🔍 DEBUG - Void failed:", errorMessage);
                        toastify(errorMessage, "error");
                    }
                } catch (error) {
                    console.error("🔍 DEBUG - Error voiding combined invoice:", error);
                    toastify("Failed to void combined invoice. Please try again.", "error");
                } finally {
                    setVoidingInvoice(false);
                }
            },
        });
    };

    const loadPropertyManagers = async () => {
        try {
            setLoadingManagers(true);
            setDebugResponse(null);

            const requestUrl = `${fetchPropertyManagersURL}/${facilityId}`;
            const response = await makeRequest2(requestUrl, "GET");
            setDebugResponse(response);

            if (!response.success) {
                setManagers([]);
                setShowSendManagerDialog(true);
                toastify("Failed to fetch property managers: " + (response.message || "Unknown error"), "error");
                return;
            }

            let managersData = [];

            if (Array.isArray(response.data)) {
                managersData = response.data;
            } else if (response.data && typeof response.data === 'object') {
                if (Array.isArray(response.data.data)) {
                    managersData = response.data.data;
                } else if (response.data.propertyManagers && Array.isArray(response.data.propertyManagers)) {
                    managersData = response.data.propertyManagers;
                } else {
                    for (const key in response.data) {
                        if (Array.isArray(response.data[key])) {
                            console.log("🔍 DEBUG - Found array property:", key);
                            managersData = response.data[key];
                            break;
                        }
                    }
                }
            }

            if (managersData && managersData.length > 0) {
                const processedManagers = managersData.map(manager => ({
                    ...manager,
                    displayLabel: `${manager.fullName || manager.name || 'Unknown'} (${manager.type || manager.role || 'User'})`
                }));

                setManagers(processedManagers);
                setShowSendManagerDialog(true);
            } else {
                setManagers([]);
                setShowSendManagerDialog(true);
                toastify("No property managers found for this facility", "warning");
            }
        } catch (error) {
            setManagers([]);
            setShowSendManagerDialog(true);
            toastify("Failed to fetch property managers: " + (error.message || "Unknown error"), "error");
        } finally {
            setLoadingManagers(false);
        }
    };

    const handleSendToManager = async () => {
        if (!selectedManager) {
            toastify("Please select a property manager", "warning");
            return;
        }

        try {
            setSendingInvoice(true);

            // Calculate total due
            const calculateTotalDue = () => {
                if (!invoice) return 0;
                const subtotal = invoice.invoices?.reduce((sum, inv) => sum + (parseFloat(inv.amount) || 0), 0) || 0;
                const totalTax = invoice.invoices?.reduce((sum, inv) => sum + (parseFloat(inv.tax) || 0), 0) || 0;
                const totalBalanceBroughtForward = parseFloat(invoice.totalBalanceBroughtForward) || 0;
                const amountPaid = parseFloat(invoice.amountPaid) || 0;
                return subtotal + totalTax + totalBalanceBroughtForward - amountPaid;
            };

            const payload = {
                managerId: selectedManager._id,
                email: selectedManager.email,
                invoiceId: invoice._id,
                managerName: selectedManager.fullName,
                invoiceNumber: invoice.combinedInvoiceNumber,
                clientName: `${invoice.customer?.firstName || ''} ${invoice.customer?.lastName || ''}`.trim() || 'N/A',
                unitName: invoice.unit?.name || 'N/A',
                amount: calculateTotalDue(),
                dueDate: invoice.dueDate,
                invoiceType: 'combined'
            };

            console.log("🔍 DEBUG - Sending combined invoice with payload:", payload);

            const sendUrl = `${sendInvoiceToManager}/${facilityId}`;
            console.log("🔍 DEBUG - Send URL:", sendUrl);

            const response = await makeRequest2(sendUrl, "POST", payload);
            console.log("🔍 DEBUG - Send invoice response:", response);

            if (response.success) {
                toastify("Combined invoice sent to property manager successfully", "success");
                setShowSendManagerDialog(false);
                setSelectedManager(null);
            } else {
                toastify(response.message || "Failed to send combined invoice", "error");
            }
        } catch (error) {
            console.error("🔍 DEBUG - Error sending combined invoice:", error);
            toastify("Failed to send combined invoice to property manager", "error");
        } finally {
            setSendingInvoice(false);
        }
    };

    const handleResendInvoice = async () => {
        if (!invoice || !invoice.customer) {
            toastify("Invalid invoice data for resending", "error");
            return;
        }

        try {
            setResending(true);
            toastify("Sending combined invoice notification...", "info");

            // Calculate total due
            const calculateTotalDue = () => {
                if (!invoice) return 0;
                const subtotal = invoice.invoices?.reduce((sum, inv) => sum + (parseFloat(inv.amount) || 0), 0) || 0;
                const totalTax = invoice.invoices?.reduce((sum, inv) => sum + (parseFloat(inv.tax) || 0), 0) || 0;
                const totalBalanceBroughtForward = parseFloat(invoice.totalBalanceBroughtForward) || 0;
                const amountPaid = parseFloat(invoice.amountPaid) || 0;
                return subtotal + totalTax + totalBalanceBroughtForward - amountPaid;
            };

            const response = await makeRequest2(
                `${resendInvoiceNotification}/${facilityId}`,
                "POST",
                {
                    invoiceId: invoice._id,
                    customerEmail: invoice.customer.email || null,
                    unitName: invoice.unit?.name || "Unit",
                    facilityName: invoice.facility?.name || "Facility",
                    amount: calculateTotalDue(),
                    dueDate: invoice.dueDate,
                    invoiceNumber: invoice.combinedInvoiceNumber,
                    invoiceType: 'combined',
                    customerName: `${invoice.customer?.firstName || ''} ${invoice.customer?.lastName || ''}`.trim()
                }
            );

            if (response.success) {
                toastify(response.data?.message || "Combined invoice notification sent successfully!", "success");
            } else {
                toastify(response.error || response.message || "Failed to send combined invoice notification", "error");
            }
        } catch (error) {
            console.error("Error sending combined invoice notification:", error);
            toastify("Failed to send combined invoice notification. Please try again.", "error");
        } finally {
            setResending(false);
        }
    };

    const actionOptionTemplate = (option) => (
        <div className="dropdown-item-container">
            <i className={option.icon} style={{ marginRight: '10px' }}></i>
            <span>{option.label}</span>
        </div>
    );

    const selectedActionTemplate = (option, props) => {
        return option ? actionOptionTemplate(option) : <span>{props.placeholder}</span>;
    };

    const renderDialogFooter = () => {
        return (
            <div className="d-flex justify-content-end mt-4">
                <Button
                    label="Cancel"
                    icon="pi pi-times"
                    onClick={() => setShowSendManagerDialog(false)}
                    className="p-button-text btn btn-secondary me-2"
                    disabled={sendingInvoice}
                    type="button"
                />
                <Button
                    label={sendingInvoice ? 'Sending...' : 'Send Combined Invoice'}
                    icon="pi pi-send"
                    onClick={handleSendToManager}
                    disabled={!selectedManager || sendingInvoice}
                    className="btn btn-primary"
                    type="button"
                />
            </div>
        );
    };

    const renderAlternativeNumbersDialogFooter = () => {
        return (
            <div className="d-flex justify-content-end mt-4">
                <Button
                    label="Cancel"
                    onClick={() => setShowAlternativeNumbersDialog(false)}
                    className="p-button-text btn btn-secondary me-2"
                    disabled={sendingToAlternative}
                    type="button"
                />
                <Button
                    label={sendingToAlternative ? 'Sending...' : 'Send Invoice'}
                    onClick={handleSendToAlternativeNumbers}
                    disabled={sendingToAlternative}
                    className="btn btn-primary"
                    type="button"
                />
            </div>
        );
    };

    // Calculate total due for display
    const calculateTotalDue = () => {
        if (!invoice) return 0;
        const subtotal = invoice.invoices?.reduce((sum, inv) => sum + (parseFloat(inv.amount) || 0), 0) || 0;
        const totalTax = invoice.invoices?.reduce((sum, inv) => sum + (parseFloat(inv.tax) || 0), 0) || 0;
        const totalBalanceBroughtForward = parseFloat(invoice.totalBalanceBroughtForward) || 0;
        const amountPaid = parseFloat(invoice.amountPaid) || 0;
        return subtotal + totalTax + totalBalanceBroughtForward - amountPaid;
    };

    return (
        <>
            <div className={styles.dropdownContainer}>
                <Dropdown
                    value={selectedAction}
                    options={actions}
                    onChange={handleActionChange}
                    placeholder="Invoice Actions"
                    itemTemplate={actionOptionTemplate}
                    valueTemplate={selectedActionTemplate}
                    className="w-full md:w-14rem"
                    disabled={loading || resending || voidingInvoice || sendingToAlternative}
                />

                {(loading || resending || voidingInvoice || sendingToAlternative) && (
                    <div className={styles.loaderIndicator}>
                        <i className="pi pi-spin pi-spinner" style={{ marginLeft: '10px' }}></i>
                    </div>
                )}
            </div>

            {/* Confirm Dialog for Void Invoice */}
            <ConfirmDialog />

            <CashPaymentModal
                visible={showCashPaymentModal}
                onHide={() => setShowCashPaymentModal(false)}
                invoice={manualPaymentInvoice}
                facilityId={facilityId}
                onSuccess={onCashPaymentSuccess}
                onError={onCashPaymentError}
            />

            {/* Alternative Numbers Dialog */}
            <Dialog
                header="Send Combined Invoice to Alternative Numbers"
                visible={showAlternativeNumbersDialog}
                style={{ width: '500px' }}
                onHide={() => setShowAlternativeNumbersDialog(false)}
                footer={renderAlternativeNumbersDialogFooter()}
                closable={true}
                draggable={false}
                modal={true}
                className="alternative-numbers-dialog"
            >
                <div className="p-fluid">
                    <div className="mb-3">
                        <p className="text-muted mb-3">
                            Enter phone numbers to send the combined invoice via SMS. 
                            You can add multiple numbers by clicking the "Add Another Number" button.
                        </p>
                        
                        <div className="invoice-details-summary p-3 mb-3" style={{ 
                            background: '#f8f9fa', 
                            borderRadius: '5px',
                            border: '1px solid #dee2e6'
                        }}>
                            <h6 className="mb-2">Invoice Details:</h6>
                            <div className="row">
                                <div className="col-6">
                                    <small><strong>Invoice #:</strong> {invoice?.combinedInvoiceNumber}</small>
                                </div>
                                <div className="col-6">
                                    <small><strong>Amount:</strong> {invoice?.currency?.code || 'KES'} {calculateTotalDue().toFixed(2)}</small>
                                </div>
                                <div className="col-6">
                                    <small><strong>Unit:</strong> {invoice?.unit?.name || 'N/A'}</small>
                                </div>
                                <div className="col-6">
                                    <small><strong>Due Date:</strong> {invoice?.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : 'N/A'}</small>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="field">
                        <label htmlFor="phoneNumbers" className="font-bold">Phone Numbers</label>
                        {alternativeNumbers.map((number, index) => (
                            <div key={index} className="d-flex align-items-center mb-2">
                                <div className="flex-grow-1 me-2">
                                    <InputText
                                        id={`phoneNumber-${index}`}
                                        placeholder="Enter phone number (e.g., 0795655023)"
                                        value={number}
                                        onChange={(e) => updateNumberAtIndex(index, e.target.value)}
                                        className={`w-full ${
                                            number.trim() !== '' && !validatePhoneNumber(number) 
                                            ? 'p-invalid' 
                                            : ''
                                        }`}
                                        disabled={sendingToAlternative}
                                    />
                                    {number.trim() !== '' && !validatePhoneNumber(number) && (
                                        <small className="p-error">Invalid phone number format</small>
                                    )}
                                </div>
                                
                                {alternativeNumbers.length > 1 && (
                                    <Button
                                        icon="pi pi-trash"
                                        className="p-button-danger p-button-text p-button-sm"
                                        onClick={() => removeNumberInput(index)}
                                        disabled={sendingToAlternative}
                                        type="button"
                                        tooltip="Remove this number"
                                    />
                                )}
                            </div>
                        ))}
                        
                        <div className="mt-2">
                            <Button
                                label="Add Another Number"
                                icon="pi pi-plus"
                                className="p-button-outlined p-button-sm"
                                onClick={addNumberInput}
                                disabled={sendingToAlternative || alternativeNumbers.length >= 10}
                                type="button"
                            />
                            {alternativeNumbers.length >= 10 && (
                                <small className="p-error ml-2">Maximum 10 numbers allowed</small>
                            )}
                        </div>
                    </div>

                    <div className="mt-3">
                        <small className="text-muted">
                            <i className="pi pi-info-circle me-1"></i>
                            SMS will be sent to all valid phone numbers. Make sure to include country codes for international numbers.
                        </small>
                    </div>
                </div>
            </Dialog>

            <Dialog
                header="Send Combined Invoice to Property Manager"
                visible={showSendManagerDialog}
                style={{ width: '450px' }}
                onHide={() => setShowSendManagerDialog(false)}
                footer={renderDialogFooter()}
                closable={true}
                draggable={false}
                modal={true}
                className="property-manager-dialog"
            >
                {loadingManagers ? (
                    <div className="text-center p-4">
                        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
                        <p>Loading property managers...</p>
                    </div>
                ) : managers.length > 0 ? (
                    <div className="p-fluid">
                        <div className="field">
                            <label htmlFor="manager">Select Property Manager</label>
                            <Dropdown
                                id="manager"
                                value={selectedManager}
                                options={managers}
                                onChange={(e) => setSelectedManager(e.value)}
                                optionLabel="displayLabel"
                                placeholder="Select a property manager"
                                className="w-full"
                                filter
                                filterBy="displayLabel,email,phoneNumber"
                                emptyFilterMessage="No matching property managers found"
                            />
                        </div>
                        <div className="field mt-3">
                            {selectedManager && (
                                <div className={styles.selectedManagerDetails}>
                                    <p className="m-0"><strong>Email:</strong> {selectedManager.email}</p>
                                    {selectedManager.phoneNumber && (
                                        <p className="m-0 mt-1"><strong>Phone:</strong> {selectedManager.phoneNumber}</p>
                                    )}
                                    <p className="m-0 mt-1"><strong>Type:</strong> {selectedManager.type || selectedManager.role}</p>
                                </div>
                            )}
                        </div>
                        <small className="text-muted">
                            An email will be sent to the selected property manager with the combined invoice details.
                        </small>
                    </div>
                ) : (
                    <div className="text-center p-4">
                        <i className="pi pi-exclamation-triangle" style={{ fontSize: '2rem', color: '#FFA500' }}></i>
                        <p>No property managers found for this facility.</p>

                        {debugResponse && (
                            <div className="mt-3 text-left" style={{ background: '#f5f5f5', padding: '10px', borderRadius: '5px' }}>
                                <details>
                                    <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>Debug Response Data</summary>
                                    <pre style={{ fontSize: '0.8rem', overflow: 'auto', maxHeight: '200px' }}>
                                        {JSON.stringify(debugResponse, null, 2)}
                                    </pre>
                                </details>
                            </div>
                        )}
                    </div>
                )}
            </Dialog>
        </>
    );
};

export default CombinedInvoiceActionsDropdown;